/**
 * Splash — full-screen brand boot overlay, shown once per browser session.
 * Solid #FF0302 canvas · exact uploaded logo · centered · staggered fade-in.
 * Total lifetime ~2.4 s (in 0.9 s, hold, out 0.5 s) then unmounts.
 * No new deps; keyframes inlined in a scoped <style> tag.
 *
 * The uploaded PNG ships with a red rounded-square backdrop baked in.
 * We keep the source file untouched and knock out the red pixels **at
 * render-time** on a client-side <canvas> so only the white "B" + white
 * circular stroke remain. Runs once per session, ~1 ms per pixel column.
 */
import { useEffect, useMemo, useState } from "react";

const SESSION_FLAG = "bb_splash_shown_v1";

// Build a transparent copy of the mark by removing every red-ish pixel.
// Returns a data URL when the browser is ready, or null otherwise.
function useTransparentMark(src) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, c.width, c.height);
        const px = data.data;
        const w = c.width, h = c.height;
        const isWhiteish = (i) => px[i] > 235 && px[i + 1] > 235 && px[i + 2] > 235;
        const isRedish   = (i) => Math.min(px[i], px[i + 1], px[i + 2]) < 210;

        // Pass 1 — drop every red-ish pixel (rounded-square backdrop +
        // drop shadow + anti-aliased edge halo).
        for (let i = 0; i < px.length; i += 4) if (isRedish(i)) px[i + 3] = 0;

        // Pass 2 — from each of the four PNG corners, walk inward across
        // the top / bottom / left / right edges and knock out the pure-
        // white "canvas background" that sits *outside* the rounded
        // square (visible as small L-marks otherwise). Stops the moment
        // it hits a non-white pixel so the logo's white B & stroke stay.
        const knockRow = (y, fromLeft) => {
          const start = fromLeft ? 0 : w - 1;
          const step  = fromLeft ? 1 : -1;
          for (let x = start; x >= 0 && x < w; x += step) {
            const i = (y * w + x) * 4;
            if (!isWhiteish(i)) break;
            px[i + 3] = 0;
          }
        };
        const knockCol = (x, fromTop) => {
          const start = fromTop ? 0 : h - 1;
          const step  = fromTop ? 1 : -1;
          for (let y = start; y >= 0 && y < h; y += step) {
            const i = (y * w + x) * 4;
            if (!isWhiteish(i)) break;
            px[i + 3] = 0;
          }
        };
        for (let y = 0; y < h; y++) { knockRow(y, true); knockRow(y, false); }
        for (let x = 0; x < w; x++) { knockCol(x, true); knockCol(x, false); }

        ctx.putImageData(data, 0, 0);
        if (!cancelled) setUrl(c.toDataURL("image/png"));
      } catch { /* ignore — fall back to original */ }
    };
    img.src = src;
    return () => { cancelled = true; };
  }, [src]);
  return url;
}

export default function Splash() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return !sessionStorage.getItem(SESSION_FLAG); } catch { return true; }
  });
  const [leaving, setLeaving] = useState(false);
  const markUrl = useTransparentMark("/brand/babu-mark.png");
  const logoSrc = useMemo(() => markUrl || "/brand/babu-mark.png", [markUrl]);

  useEffect(() => {
    if (!visible) return;
    try { sessionStorage.setItem(SESSION_FLAG, "1"); } catch { /* ignore */ }
    const t1 = setTimeout(() => setLeaving(true), 3500);
    const t2 = setTimeout(() => setVisible(false), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 grid place-items-center"
      style={{
        zIndex: 9999,
        background: "#FF0302",
        opacity: leaving ? 0 : 1,
        transition: "opacity 500ms ease-out",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="brand-splash"
      aria-hidden="true"
    >
      <style>{`
        @keyframes bbSplashUp {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0);    }
        }
        .bb-splash-logo { animation: bbSplashUp 700ms ease-out both; }
        .bb-splash-name { animation: bbSplashUp 700ms ease-out 350ms both; letter-spacing: 0.14em; }
        .bb-splash-tag  { animation: bbSplashUp 700ms ease-out 700ms both; }
      `}</style>

      {/* Vertical stack — nudged slightly above center (top ~42% via translate) */}
      <div
        className="flex flex-col items-center px-6"
        style={{ transform: "translateY(-6%)" }}
      >
        {/* Uploaded logo — original file is untouched. Red pixels of the
            rounded-square backdrop are knocked out at render-time on a
            hidden canvas so only the white "B" + white circular stroke
            remain. Falls back to the raw PNG if canvas is unavailable. */}
        <img
          src={logoSrc}
          alt="Babu Baazar"
          className="bb-splash-logo select-none"
          style={{
            width: "min(52vw, 240px)",
            height: "auto",
            display: "block",
          }}
          draggable={false}
        />

        <h1
          className="bb-splash-name text-white font-bold text-center"
          style={{
            marginTop: "clamp(20px, 4vh, 40px)",
            fontSize: "clamp(24px, 6.4vw, 40px)",
            lineHeight: 1.05,
            fontFamily:
              "'Playfair Display', 'DM Serif Display', ui-serif, Georgia, serif",
          }}
        >
          BABU BAAZAR
        </h1>

        <div
          className="bb-splash-tag inline-flex items-center justify-center text-white"
          style={{
            marginTop: "clamp(14px, 2.6vh, 22px)",
            padding: "8px 18px",
            border: "1.5px solid rgba(255,255,255,0.85)",
            borderRadius: 999,
            fontSize: "clamp(12px, 3.6vw, 15px)",
            letterSpacing: "0.02em",
            fontWeight: 500,
          }}
        >
          Trusted by India&rsquo;s Families
        </div>
      </div>
    </div>
  );
}
